import type { ECharts } from 'echarts'

/** ECharts time-axis candlesticks: milliseconds, open, close, low, high. */
export type CandleGeometry = [number, number | null, number | null, number | null, number | null]
type Project = (timestamp: number, value: number) => readonly number[]

/** Distance to a real body or wick; missing buckets never become connecting lines. */
export function candleDistanceAt(
  candles: readonly CandleGeometry[], timestamp: number, value: number,
  project: Project = (time, level) => [time, level], bodyWidth = 0,
): number | null {
  if (!candles.length || !Number.isFinite(timestamp) || !Number.isFinite(value)) return null
  const cursor = project(timestamp, value)
  if (!cursor.every(Number.isFinite)) return null
  let low = 0
  let high = candles.length
  while (low < high) {
    const middle = (low + high) >>> 1
    if (candles[middle][0] < timestamp) low = middle + 1
    else high = middle
  }
  let distance = Infinity
  // The two adjoining candles are enough to identify the drawn series at this
  // time; skip null placeholders without treating their values as observations.
  for (const direction of [-1, 1]) {
    for (let index = direction < 0 ? low - 1 : low; index >= 0 && index < candles.length; index += direction) {
      const candle = candles[index]
      if (!candle.every((coordinate) => coordinate != null && Number.isFinite(coordinate))) continue
      const [time, open, close, bottom, top] = candle as readonly number[]
      if (bottom > Math.min(open, close) || top < Math.max(open, close)) continue
      const start = project(time, open)
      const end = project(time, close)
      const wickLow = project(time, bottom)
      const wickHigh = project(time, top)
      if (![...start, ...end, ...wickLow, ...wickHigh].every(Number.isFinite)) continue
      const outside = (level: number, a: number, b: number) =>
        Math.max(Math.min(a, b) - level, 0, level - Math.max(a, b))
      const dx = Math.abs(cursor[0] - start[0])
      const wickDistance = Math.hypot(dx, outside(cursor[1], wickLow[1], wickHigh[1]))
      const bodyDistance = Math.hypot(Math.max(0, dx - Math.max(0, bodyWidth) / 2), outside(cursor[1], start[1], end[1]))
      distance = Math.min(distance, wickDistance, bodyDistance)
      break
    }
  }
  return Number.isFinite(distance) ? distance : null
}

export function chartCandleDistanceAt(
  chart: ECharts | undefined, exchange: string, candles: readonly CandleGeometry[], timestamp: number, value: number,
): number | null {
  if (!chart) return candleDistanceAt(candles, timestamp, value)
  // Read ECharts' laid-out width so zoomed or dense candles use their actual
  // body geometry. Older/mock instances can still match their wick exactly.
  const model = (chart as unknown as { getModel?: () => {
    getSeriesByName?: (name: string) => Array<{ getData: () => { getLayout: (name: string) => unknown } }>
  } }).getModel?.()
  const width = Number(model?.getSeriesByName?.(exchange)[0]?.getData().getLayout('candleWidth'))
  return candleDistanceAt(candles, timestamp, value,
    (time, level) => chart.convertToPixel({ gridIndex: 0 }, [time, level]),
    Number.isFinite(width) ? width : 0)
}
